/**
 * Navigation Icons - Professional minimalistic icons for navigation
 * Now using Lucide React icons for consistency and modern design
 */

import React from 'react';
import {
  Home,
  Calendar,
  BookOpen,
  Target,
  LayoutDashboard,
  Building2,
  Megaphone,
  BarChart2,
  Users,
  ClipboardCheck,
  Activity,
  Inbox,
  TrendingUp,
  User,
  LogOut,
  Settings,
  FileText,
  FileSignature,
  Clock,
  Timer,
  Menu,
  X,
  ChevronDown,
  Sun,
  Moon,
  Droplet,
  Edit,
  Trash2,
  Plus,
  Eye,
  Check,
  Ban,
  Copy,
  Download,
  Upload,
  Search,
  Filter,
  RefreshCw,
  Shield,
  ClipboardList,
  CheckCircle,
  DoorOpen,
  Mail,
  Star,
  Bell,
  MapPin,
  Pause,
  Repeat,
  UserPlus,
  Image
} from 'lucide-react';

import IconWrapper from '../IconWrapper/IconWrapper';

// Home icon
export const HomeIcon = () => (
  <IconWrapper>
    <Home size={20} />
  </IconWrapper>
);

// Programs icon
export const ProgramsIcon = () => (
  <IconWrapper>
    <Calendar size={20} />
  </IconWrapper>
);

// About/Story icon
export const BookIcon = () => (
  <IconWrapper>
    <BookOpen size={20} />
  </IconWrapper>
);

// Approach icon
export const TargetIcon = () => (
  <IconWrapper>
    <Target size={20} />
  </IconWrapper>
);

// Dashboard icon
export const DashboardIcon = () => (
  <IconWrapper>
    <LayoutDashboard size={20} />
  </IconWrapper>
);

// Club Management icon
export const BuildingIcon = () => (
  <IconWrapper>
    <Building2 size={20} />
  </IconWrapper>
);

// Training/Coach icon
export const TrainingIcon = () => (
  <IconWrapper>
    <Megaphone size={20} />
  </IconWrapper>
);

// Evaluations/Chart icon
export const ChartIcon = () => (
  <IconWrapper>
    <BarChart2 size={20} />
  </IconWrapper>
);

// Teams/Group icon
export const TeamIcon = () => (
  <IconWrapper>
    <Users size={20} />
  </IconWrapper>
);

// Skills/Assessment icon
export const ChecklistIcon = () => (
  <IconWrapper>
    <ClipboardCheck size={20} />
  </IconWrapper>
);

// Skills/Soccer ball variant -> Activity as placeholder
export const SoccerIcon = () => (
  <IconWrapper>
    <Activity size={20} />
  </IconWrapper>
);

// Inbox icon
export const InboxIcon = () => (
  <IconWrapper>
    <Inbox size={20} />
  </IconWrapper>
);

// Growth/Progress icon
export const GrowthIcon = () => (
  <IconWrapper>
    <TrendingUp size={20} />
  </IconWrapper>
);

// Profile/User icon
export const ProfileIcon = () => (
  <IconWrapper>
    <User size={20} />
  </IconWrapper>
);

// Join Us/User Plus icon
export const JoinTeamIcon = () => (
  <IconWrapper>
    <UserPlus size={20} />
  </IconWrapper>
);

// Sign Out/Exit icon
export const SignOutIcon = () => (
  <IconWrapper>
    <LogOut size={20} />
  </IconWrapper>
);

// Logo Soccer Ball - Keeping original SVG as it is likely a specific brand asset
export const LogoSoccerIcon = () => (
  <svg
    width="32"
    height="32"
    viewBox="0 0 512 512"
    xmlns="http://www.w3.org/2000/svg"
    className="logo-icon-svg"
    aria-hidden="true"
  >
    {/* Outer circle */}
    <circle cx="256" cy="256" r="210" fill="#ffffff" stroke="#000000" strokeWidth="6" />

    {/* White seams */}
    <g fill="none" stroke="#ffffff" strokeLinecap="round" strokeLinejoin="round" strokeWidth="14">
      <path d="M256 56 L256 196" />
      <path d="M256 456 L256 316" />
      <path d="M464 256 L324 256" />
      <path d="M48 256 L188 256" />
      <path d="M392 104 L284 220" />
      <path d="M120 392 L228 276" />
      <path d="M392 408 L284 292" />
      <path d="M120 120 L228 236" />
    </g>

    {/* Pentagon outlines (no fill) */}
    <g fill="none" stroke="#000000" strokeWidth="10" strokeLinejoin="round">
      {/* Central pentagon */}
      <polygon points="256.000,196.000 313.063,237.459 291.267,304.541 220.733,304.541 198.937,237.459" />

      {/* Surrounding pentagons */}
      <polygon points="256.000,181.000 208.447,146.451 226.611,90.549 285.389,90.549 303.553,146.451" />
      <polygon points="327.329,232.824 345.493,176.922 404.271,176.922 422.435,232.824 374.882,267.373" />
      <polygon points="300.084,316.676 358.862,316.676 377.026,372.578 329.473,407.127 281.920,372.578" />
      <polygon points="211.916,316.676 230.080,372.578 182.527,407.127 134.974,372.578 153.138,316.676" />
      <polygon points="184.671,232.824 137.118,267.373 89.565,232.824 107.729,176.922 166.507,176.922" />
    </g>

    {/* Optional highlight */}
    <circle cx="256" cy="256" r="6" fill="#ffffff" />
  </svg>
);

// Settings/Gear icon
export const SettingsIcon = () => (
  <IconWrapper>
    <Settings size={20} />
  </IconWrapper>
);

// Users/Group icon
export const UsersIcon = () => (
  <IconWrapper>
    <Users size={20} />
  </IconWrapper>
);

// Document/File icon
export const DocumentIcon = () => (
  <IconWrapper>
    <FileText size={20} />
  </IconWrapper>
);

// Progress/Stats icon
export const ProgressIcon = () => (
  <IconWrapper>
    <TrendingUp size={20} />
  </IconWrapper>
);

// Calendar icon
export const CalendarIcon = () => (
  <IconWrapper>
    <Calendar size={20} />
  </IconWrapper>
);

// Gallery/Image icon
export const ImageIcon = () => (
  <IconWrapper>
    <Image size={20} />
  </IconWrapper>
);

// Seasons/Time icon
export const SeasonsIcon = () => (
  <IconWrapper>
    <Clock size={20} />
  </IconWrapper>
);

// Tryouts/Runner icon
export const TryoutsIcon = () => (
  <IconWrapper>
    <Timer size={20} />
  </IconWrapper>
);

// Menu/Hamburger icon
export const MenuIcon = () => (
  <IconWrapper>
    <Menu size={20} />
  </IconWrapper>
);

// Close/X icon
export const CloseIcon = () => (
  <IconWrapper>
    <X size={20} />
  </IconWrapper>
);

// Chevron Down icon
export const ChevronDownIcon = () => (
  <IconWrapper>
    <ChevronDown size={20} />
  </IconWrapper>
);

// Sun icon - Light Mode
export const SunIcon = () => (
  <IconWrapper>
    <Sun size={20} />
  </IconWrapper>
);

// Moon icon - Dark Mode
export const MoonIcon = () => (
  <IconWrapper>
    <Moon size={20} />
  </IconWrapper>
);

// Droplet icon
export const DropletIcon = () => (
  <IconWrapper>
    <Droplet size={20} />
  </IconWrapper>
);

// Droplets icon
export const DropletsIcon = () => (
  <IconWrapper>
    <div style={{ position: 'relative' }}>
      <Droplet size={16} style={{ position: 'absolute', top: -4, left: -4 }} />
      <Droplet size={16} />
    </div>
  </IconWrapper>
);

// ===== ACTION ICONS =====

// Edit/Pencil icon
export const EditIcon = () => (
  <IconWrapper>
    <Edit size={20} />
  </IconWrapper>
);

// Delete/Trash icon
export const DeleteIcon = () => (
  <IconWrapper>
    <Trash2 size={20} />
  </IconWrapper>
);

// Add/Plus icon
export const AddIcon = () => (
  <IconWrapper>
    <Plus size={20} />
  </IconWrapper>
);

// View/Eye icon
export const ViewIcon = () => (
  <IconWrapper>
    <Eye size={20} />
  </IconWrapper>
);

// Save/Check icon
export const SaveIcon = () => (
  <IconWrapper>
    <Check size={20} />
  </IconWrapper>
);

// Cancel/X icon
export const CancelIcon = () => (
  <IconWrapper>
    <Ban size={20} />
  </IconWrapper>
);

// Copy/Duplicate icon
export const CopyIcon = () => (
  <IconWrapper>
    <Copy size={20} />
  </IconWrapper>
);

// Download icon
export const DownloadIcon = () => (
  <IconWrapper>
    <Download size={20} />
  </IconWrapper>
);

// Upload icon
export const UploadIcon = () => (
  <IconWrapper>
    <Upload size={20} />
  </IconWrapper>
);

// Search/Magnifying glass icon
export const SearchIcon = () => (
  <IconWrapper>
    <Search size={20} />
  </IconWrapper>
);

// Filter icon
export const FilterIcon = () => (
  <IconWrapper>
    <Filter size={20} />
  </IconWrapper>
);

// Refresh/Reload icon
export const RefreshIcon = () => (
  <IconWrapper>
    <RefreshCw size={20} />
  </IconWrapper>
);

// Shield/Protection icon
export const ShieldIcon = () => (
  <IconWrapper>
    <Shield size={20} />
  </IconWrapper>
);

// Clipboard/Checklist icon
export const ClipboardIcon = () => (
  <IconWrapper>
    <ClipboardList size={20} />
  </IconWrapper>
);

// Checkmark/Success icon
export const CheckmarkIcon = () => (
  <IconWrapper>
    <CheckCircle size={20} />
  </IconWrapper>
);

// Door/Gate icon
export const DoorIcon = () => (
  <IconWrapper>
    <DoorOpen size={20} />
  </IconWrapper>
);

// Inbox/Mail icon
export const MailIcon = () => (
  <IconWrapper>
    <Mail size={20} />
  </IconWrapper>
);

// Star/Sparkle icon
export const StarIcon = () => (
  <IconWrapper>
    <Star size={20} />
  </IconWrapper>
);

// Bell/Notification icon
export const BellIcon = () => (
  <IconWrapper>
    <Bell size={20} />
  </IconWrapper>
);

// Pin/Location icon
export const PinIcon = () => (
  <IconWrapper>
    <MapPin size={20} />
  </IconWrapper>
);

// Block/Prohibition icon
export const BlockIcon = () => (
  <IconWrapper>
    <Ban size={20} />
  </IconWrapper>
);

// Pause icon
export const PauseIcon = () => (
  <IconWrapper>
    <Pause size={20} />
  </IconWrapper>
);

// Repeat/Cycle icon
export const RepeatIcon = () => (
  <IconWrapper>
    <Repeat size={20} />
  </IconWrapper>
);

// Map icon names to components
export const NAVIGATION_ICONS = {
  // Navigation icons
  home: HomeIcon,
  programs: ProgramsIcon,
  soccer: SoccerIcon,
  book: BookIcon,
  target: TargetIcon,
  dashboard: DashboardIcon,
  building: BuildingIcon,
  training: TrainingIcon,
  chart: ChartIcon,
  team: TeamIcon,
  checklist: ChecklistIcon,
  inbox: InboxIcon,
  growth: GrowthIcon,
  profile: ProfileIcon,
  signout: SignOutIcon,
  logo: LogoSoccerIcon,
  settings: SettingsIcon,
  users: UsersIcon,
  document: DocumentIcon,
  progress: ProgressIcon,
  calendar: CalendarIcon,
  image: ImageIcon,
  seasons: SeasonsIcon,
  tryouts: TryoutsIcon,
  // System icons
  sun: SunIcon,
  moon: MoonIcon,
  droplet: DropletIcon,
  droplets: DropletsIcon,
  // Action icons
  edit: EditIcon,
  delete: DeleteIcon,
  add: AddIcon,
  view: ViewIcon,
  save: SaveIcon,
  cancel: CancelIcon,
  copy: CopyIcon,
  download: DownloadIcon,
  upload: UploadIcon,
  search: SearchIcon,
  filter: FilterIcon,
  refresh: RefreshIcon,
  shield: ShieldIcon,
  clipboard: ClipboardIcon,
  checkmark: CheckmarkIcon,
  door: DoorIcon,
  mail: MailIcon,
  star: StarIcon,
  bell: BellIcon,
  pin: PinIcon,
  block: BlockIcon,
  pause: PauseIcon,
  repeat: RepeatIcon,
  'join-team': JoinTeamIcon
};

// Helper function to get icon by name
export const getNavigationIcon = (iconName) => {
  const Icon = NAVIGATION_ICONS[iconName];
  return Icon ? <Icon /> : null;
};

export default NAVIGATION_ICONS;
